import React, { useEffect, useRef, useState } from 'react';
import api from '../lib/api';
import { StarRating } from './StarRating';
import { UserAvatar } from './VapeAvatars';
import { Quote, Star, ShieldCheck } from 'lucide-react';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return ''; }
};

const ReviewCard = ({ r }) => (
  <article className="relative flex flex-col rounded-2xl border border-white/10 bg-gradient-to-br from-neutral-900 via-neutral-950 to-black p-6 shadow-xl w-[340px] shrink-0 h-full min-h-[240px] transition-all hover:border-orange-500/50 hover:shadow-orange-500/10">
    <Quote className="absolute -top-4 left-6 w-9 h-9 text-orange-500 fill-orange-500 drop-shadow-[0_2px_10px_rgba(249,115,22,0.4)]" />
    <div className="flex items-center gap-3 mb-3">
      <UserAvatar picture={r.author_picture} name={r.author_name} size={44} />
      <div className="min-w-0 flex-1">
        <p className="font-bold text-white truncate flex items-center gap-1.5">
          {r.author_name}
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" title="Verificada" />
        </p>
        <p className="text-[10px] text-white/40 uppercase tracking-wider font-semibold">{formatDate(r.created_at)}</p>
      </div>
    </div>
    <StarRating value={r.rating} size={16} />
    <h4 className="mt-2.5 font-black text-white text-[15px] leading-snug line-clamp-1">{r.title}</h4>
    <p className="text-white/70 leading-relaxed text-[13.5px] mt-2 line-clamp-4">“{r.comment}”</p>
  </article>
);

const Testimonials = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ total: 0, average: 0 });
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    api.get('/reviews?limit=30')
      .then((r) => {
        // Only the highest rated: 4+ stars, sorted desc by rating then date
        const top = (r.data || [])
          .filter((x) => x.rating >= 4)
          .sort((a, b) => (b.rating - a.rating) || (new Date(b.created_at) - new Date(a.created_at)));
        setReviews(top);
      })
      .catch(() => {});
    api.get('/reviews/stats').then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!reviews.length) return null;

  // Duplicate for seamless loop
  const marqueeReviews = [...reviews, ...reviews];
  // Auto-adjust animation speed based on count
  const durationSec = Math.max(30, reviews.length * 8);

  return (
    <section className="relative mt-24 pb-4" data-testid="testimonials-section">
      {/* Ambient orange glow */}
      <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-orange-500/5 via-transparent to-transparent pointer-events-none" />

      {/* Header */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 relative">
        <div className="flex flex-col items-center text-center mb-10">
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] font-black text-orange-400 bg-orange-500/10 border border-orange-500/30 px-3.5 py-1.5 rounded-full">
            <Star className="w-3 h-3 fill-orange-400 text-orange-400" /> Reseñas verificadas
          </span>
          <h2 className="mt-4 text-4xl md:text-6xl font-black text-white tracking-tight leading-[1.05]">
            Lo que dicen <br className="md:hidden" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-orange-500 to-red-500">nuestros vapers</span>
          </h2>

          <div className="mt-5 flex items-center gap-3 rounded-full bg-neutral-950 border border-white/10 px-4 py-2 shadow-lg">
            <div className="flex items-baseline gap-0.5">
              <span className="text-2xl font-black text-white">{stats.average.toFixed(1)}</span>
              <span className="text-white/40 text-xs font-bold">/5</span>
            </div>
            <div className="w-px h-5 bg-white/10" />
            <StarRating value={Math.round(stats.average)} size={16} />
            <div className="w-px h-5 bg-white/10" />
            <span className="text-[11px] text-white/60 font-semibold">
              {stats.total} {stats.total === 1 ? 'reseña' : 'reseñas'}
            </span>
          </div>
        </div>
      </div>

      {/* Marquee carousel — edge to edge */}
      <div
        className="relative w-full overflow-hidden py-6"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Edge fade masks */}
        <div className="absolute inset-y-0 left-0 w-24 md:w-40 bg-gradient-to-r from-black to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-24 md:w-40 bg-gradient-to-l from-black to-transparent z-10 pointer-events-none" />

        <style>{`
          @keyframes va-marquee {
            0%   { transform: translate3d(0,0,0); }
            100% { transform: translate3d(-50%,0,0); }
          }
          .va-marquee-track {
            animation: va-marquee ${durationSec}s linear infinite;
          }
          .va-marquee-paused { animation-play-state: paused !important; }
        `}</style>

        <div className={`va-marquee-track ${paused ? 'va-marquee-paused' : ''} flex gap-6 w-max`}>
          {marqueeReviews.map((r, i) => (
            <ReviewCard key={`${r.id}-${i}`} r={r} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
